import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  TextField,
  Collapse,
  styled,
  alpha,
  CircularProgress,
  Tooltip,
  Badge,
  useMediaQuery,
  useTheme,
  SwipeableDrawer,
  Skeleton
} from '@mui/material';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import FavoriteIcon from '@mui/icons-material/Favorite';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import HomeIcon from '@mui/icons-material/Home';
import EmailIcon from '@mui/icons-material/Email';
import * as Icons from '@mui/icons-material';
import Img from '../../logob.png';
import { useThemeMode } from '../../App';
import FormatIndentDecreaseIcon from '@mui/icons-material/FormatIndentDecrease';
import FormatIndentIncreaseIcon from '@mui/icons-material/FormatIndentIncrease';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';

const drawerWidth = 280;

const Logo = styled('img')(({ theme }) => ({
  height: '40px',
  width: 'auto',
  transition: 'transform 0.3s ease',
  filter: theme.palette.mode === 'dark' ? 'brightness(1.2)' : 'none',
  '&:hover': {
    transform: 'scale(1.05)',
  }
}));

interface StyledAppBarProps {
  drawerwidth: number;
}

const StyledAppBar = styled(AppBar)<StyledAppBarProps>(({ theme, drawerwidth }) => ({
  background: `linear-gradient(to right, ${alpha(theme.palette.background.paper, 0.8)}, ${alpha(theme.palette.background.paper, 0.85)}, ${alpha(theme.palette.background.paper, 0.8)})`,
  backdropFilter: 'blur(16px)',
  boxShadow: 'none',
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
  color: theme.palette.text.primary,
  transition: theme.transitions.create(['width', 'margin', 'background'], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.standard,
  }),
  [theme.breakpoints.up('sm')]: {
    width: `calc(100% - ${drawerwidth}px)`,
    marginLeft: `${drawerwidth}px`,
  }
}));

// Компонент полосы прогресса прокрутки
const ScrollProgressBar = styled('div')<{ progress: number }>(({ theme, progress }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  height: '3px',
  width: `${progress}%`,
  background: theme.palette.mode === 'dark'
    ? `linear-gradient(to right, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`
    : `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  transition: 'width 0.2s ease',
  zIndex: 100,
  boxShadow: `0 0 8px ${alpha(theme.palette.primary.main, 0.5)}`,
}));

const SearchTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.shape.borderRadius * 3,
    backgroundColor: alpha(theme.palette.background.paper, 0.6),
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: alpha(theme.palette.background.paper, 0.8),
      transform: 'translateY(-1px)',
    },
    '&.Mui-focused': {
      backgroundColor: alpha(theme.palette.background.paper, 1),
      transform: 'translateY(-1px)',
      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
    }
  },
}));

const NavIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
  transition: 'transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), color 0.2s ease, background-color 0.2s ease',
  margin: theme.spacing(0, 0.5),
  borderRadius: theme.shape.borderRadius * 1.5,
  '&:hover': {
    color: theme.palette.primary.main,
    transform: 'translateY(-3px) scale(1.08)',
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
  '&:active': {
    transform: 'translateY(0) scale(0.96)',
  },
}));

const NavLinkIndicator = styled('span')<{ isactive: string }>(({ theme, isactive }) => ({
  position: 'absolute',
  bottom: '4px',
  left: '50%',
  transform: 'translateX(-50%)',
  width: isactive === 'true' ? '24px' : '0px',
  height: '3px',
  borderRadius: '2px',
  background: theme.palette.primary.main,
  transition: 'width 0.3s cubic-bezier(0.65, 0, 0.35, 1)',
}));

interface Category {
  id: number;
  title: string;
  description: string | null;
  display_order: number;
  subcategories_count: number;
  icon?: string;
}

interface Subcategory {
  id: number;
  category_id: number;
  title: string;
  display_order: number;
  articles_count: number;
}

const ActiveItemBackground = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: 0,
  right: 0,
  top: 0,
  bottom: 0,
  borderRadius: theme.shape.borderRadius * 2,
  zIndex: -1,
  background: `linear-gradient(145deg, 
    ${alpha(theme.palette.primary.main, 0.12)}, 
    ${alpha(theme.palette.primary.main, 0.08)})`,
  boxShadow: `0 4px 15px -4px ${alpha(theme.palette.primary.main, 0.12)}`,
  transition: 'all 0.25s ease-in-out',
  opacity: 0,
  transform: 'scale(0.98)',
}));

const CategoryListItem = styled(ListItemButton)(({ theme }) => ({
  position: 'relative',
  marginBottom: theme.spacing(0.5),
  borderRadius: theme.shape.borderRadius * 2,
  transition: 'all 0.25s ease-in-out',
  overflow: 'hidden',
  '&:hover': {
    backgroundColor: 'transparent',
    transform: 'translateX(4px)',
    '& .MuiListItemText-root': {
      color: theme.palette.primary.main,
    },
    '& .MuiListItemIcon-root': {
      color: theme.palette.primary.main,
    },
  },
  '&.Mui-selected': {
    backgroundColor: 'transparent',
    '& .MuiListItemText-root': {
      color: theme.palette.primary.main,
    },
    '& .MuiListItemIcon-root': {
      color: theme.palette.primary.main,
    },
    [`& ${ActiveItemBackground}`]: {
      opacity: 1,
      transform: 'scale(1)',
    },
  },
}));

// Добавляю компонент для всплывающих подсказок в компактном режиме
const CompactTooltip = styled(({ className, ...props }: any) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .MuiTooltip-tooltip`]: {
    backgroundColor: alpha(theme.palette.background.paper, 0.9),
    color: theme.palette.text.primary,
    boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
    borderRadius: theme.shape.borderRadius * 1.5,
    fontSize: '0.9rem',
    padding: theme.spacing(1, 1.5),
    border: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
    backdropFilter: 'blur(8px)',
  },
}));

const Layout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openCategories, setOpenCategories] = useState<number[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<{ [key: number]: Subcategory[] }>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { mode, toggleTheme } = useThemeMode();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [drawerWidth, setDrawerWidth] = useState(280);
  const [isCompactMode, setIsCompactMode] = useState(false);
  // Добавляем состояние для прогресса прокрутки
  const [scrollProgress, setScrollProgress] = useState(0);

  // Кэширование категорий
  const CACHE_KEY = 'zerde_categories_cache';
  const CACHE_EXPIRY = 1000 * 60 * 60; // 1 час

  // Определяем, есть ли у пользователя избранные статьи
  const [hasFavorites, setHasFavorites] = useState(false);

  useEffect(() => {
    // Проверяем наличие избранных статей в localStorage
    const favorites = localStorage.getItem('favorites');
    setHasFavorites(!!favorites && JSON.parse(favorites).length > 0);
  }, [location.pathname]);

  useEffect(() => {
    // Для сохранения состояния компактного режима
    const savedMode = localStorage.getItem('compact_sidebar');
    if (savedMode) {
      const isCompact = savedMode === 'true';
      setIsCompactMode(isCompact);
      setDrawerWidth(isCompact ? 80 : 280);
    }
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        // Проверяем наличие кэша
        const cachedData = localStorage.getItem(CACHE_KEY);
        if (cachedData) {
          const { data, timestamp } = JSON.parse(cachedData);
          // Используем кэш, если он не устарел
          if (Date.now() - timestamp < CACHE_EXPIRY) {
            setCategories(data);
            setLoading(false);
            return;
          }
        }

        // Если кэш отсутствует или устарел, загружаем данные с API
        const response = await fetch('https://guide.zerdegroup.kz/api/categories/with-subcategories/count');
        if (!response.ok) throw new Error('Ошибка при загрузке категорий');
        const data = await response.json();

        // Сохраняем в кэш
        localStorage.setItem(CACHE_KEY, JSON.stringify({
          data,
          timestamp: Date.now()
        }));

        setCategories(data);
      } catch (err) {
        setError('Ошибка при загрузке категорий');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    const fetchSubcategories = async (categoryId: number) => {
      try {
        const response = await fetch(`https://guide.zerdegroup.kz/api/subcategories/category/${categoryId}`);
        if (!response.ok) throw new Error('Ошибка при загрузке подкатегорий');
        const data = await response.json();
        setSubcategories(prev => ({ ...prev, [categoryId]: data }));
      } catch (err) {
        console.error(err);
      }
    };

    const initializeData = async () => {
      setLoading(true);
      await fetchCategories();
    };

    initializeData();
  }, []);

  useEffect(() => {
    // Для сохранения состояния компактного режима
    const savedMode = localStorage.getItem('compact_sidebar');
    if (savedMode) {
      const isCompact = savedMode === 'true';
      setIsCompactMode(isCompact);
      setDrawerWidth(isCompact ? 80 : 280);
    }
  }, []);

  const toggleCompactMode = () => {
    const newMode = !isCompactMode;
    setIsCompactMode(newMode);
    setDrawerWidth(newMode ? 80 : 280);
    localStorage.setItem('compact_sidebar', newMode.toString());
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleCategoryClick = async (categoryId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    if (!subcategories[categoryId]) {
      try {
        const response = await fetch(`https://guide.zerdegroup.kz/api/subcategories/category/${categoryId}`);
        if (!response.ok) throw new Error('Ошибка при загрузке подкатегорий');
        const data = await response.json();
        setSubcategories(prev => ({ ...prev, [categoryId]: data }));
      } catch (err) {
        console.error(err);
      }
    }
    setOpenCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleCategoryNavigate = (categoryId: number) => {
    navigate(`/category/${categoryId}`);
    if (isMobile) setMobileOpen(false);
  };

  const handleSubcategoryClick = (categoryId: number, subcategoryId: number) => {
    navigate(`/category/${categoryId}/${subcategoryId}`);
    if (isMobile) setMobileOpen(false);
  };

  const getCategoryIcon = (icon: string | undefined) => {
    if (!icon) return <Icons.Code />;

    // Преобразуем имя иконки из camelCase в PascalCase для поиска в Icons
    const iconName = icon.split(/(?=[A-Z])/).map(part =>
      part.charAt(0).toUpperCase() + part.slice(1)
    ).join('');

    const Icon = (Icons as any)[iconName] || Icons.Code;
    return <Icon />;
  };

  // Добавляем функцию для определения активной подкатегории
  const isSubcategoryActive = (categoryId: number, subcategoryId: number) => {
    const pathParts = location.pathname.split('/');
    const urlCategoryId = pathParts[2];
    const urlSubcategoryId = pathParts[3];

    return urlCategoryId === categoryId.toString() &&
      urlSubcategoryId === subcategoryId.toString();
  };

  // Добавляем функцию для определения активной категории
  const isCategoryActive = (categoryId: number) => {
    const pathParts = location.pathname.split('/');
    const urlCategoryId = pathParts[2];
    const urlSubcategoryId = pathParts[3];

    return urlCategoryId === categoryId.toString() && !urlSubcategoryId;
  };

  // Используем useMemo для скелетона категорий
  const categorySkeleton = useMemo(() => {
    return Array(6).fill(0).map((_, index) => (
      <Box key={index} sx={{ mb: 1, px: isCompactMode ? 0 : 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', py: 1, px: isCompactMode ? 1 : 0 }}>
          <Skeleton variant="circular" width={24} height={24}
            sx={{ mr: isCompactMode ? 0 : 2, minWidth: isCompactMode ? 24 : 36 }} />
          {!isCompactMode &&
            <Skeleton variant="text" width={`${Math.random() * 40 + 50}%`} height={24} />
          }
        </Box>
      </Box>
    ));
  }, [isCompactMode]);

  // Отслеживаем прокрутку страницы
  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const scrollPosition = window.scrollY;

      if (totalHeight > 0) {
        // Рассчитываем процент прокрутки и ограничиваем его от 0 до 100
        const progress = Math.min(100, Math.max(0, (scrollPosition / totalHeight) * 100));
        setScrollProgress(progress);
      } else {
        setScrollProgress(0);
      }
    };

    // Добавляем слушатель события прокрутки
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Вызываем однократно для инициализации
    handleScroll();

    // Очищаем слушатель при размонтировании
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [location.pathname]); // Пересчитываем при изменении маршрута

  const drawer = (
    <Box sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: theme.palette.background.paper,
      position: 'relative',
      transition: 'all 0.3s ease',
    }}>
      <Toolbar sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        px: isCompactMode ? 0.5 : 2,
        py: isCompactMode ? 1 : undefined,
      }}>
        <Logo
          src={Img}
          alt="Zerde"
          onClick={() => {
            navigate('/');
            if (isMobile) setMobileOpen(false);
          }}
          sx={{
            py: !isCompactMode ? 1 : undefined,
            cursor: 'pointer',
            height: isCompactMode ? '100%' : '100%',
            maxWidth: isCompactMode ? '40px' : '100%',
            objectFit: 'contain',
          }}
        />
      </Toolbar>

      {!isCompactMode && (
        <Box sx={{
          px: 2,
          pt: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Tooltip title="Поиск по статьям">
            <IconButton
              onClick={() => navigate('/search')}
              sx={{
                width: '100%',
                justifyContent: 'flex-start',
                borderRadius: 2,
                p: 1,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  transform: 'translateX(4px)',
                }
              }}
            >
              <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography color="text.secondary">Поиск по статьям...</Typography>
            </IconButton>
          </Tooltip>
        </Box>
      )}

      <Box sx={{ p: isCompactMode ? 0.5 : 2 }}>
        <CategoryListItem
          onClick={() => {
            navigate('/');
            if (isMobile) setMobileOpen(false);
          }}
          selected={location.pathname === '/'}
          sx={{
            py: isCompactMode ? 1.5 : undefined,
            justifyContent: isCompactMode ? 'center' : 'flex-start',
          }}
        >
          <ActiveItemBackground />
          <ListItemIcon sx={{
            color: location.pathname === '/' ? 'primary.main' : 'text.secondary',
            minWidth: isCompactMode ? 0 : 36,
            mr: isCompactMode ? 0 : 2,
            justifyContent: isCompactMode ? 'center' : 'flex-start'
          }}>
            <HomeIcon />
          </ListItemIcon>
          {!isCompactMode && <ListItemText primary="Главная" />}
          {isCompactMode && (
            <CompactTooltip title="Главная" placement="right">
              <span />
            </CompactTooltip>
          )}
        </CategoryListItem>

        <CategoryListItem
          onClick={() => {
            navigate('/favorites');
            if (isMobile) setMobileOpen(false);
          }}
          selected={location.pathname === '/favorites'}
          sx={{
            py: isCompactMode ? 1.5 : undefined,
            mb: 2,
            justifyContent: isCompactMode ? 'center' : 'flex-start',
          }}
        >
          <ActiveItemBackground />
          <ListItemIcon sx={{
            color: location.pathname === '/favorites' ? 'primary.main' : 'text.secondary',
            minWidth: isCompactMode ? 0 : 36,
            mr: isCompactMode ? 0 : 2,
            justifyContent: isCompactMode ? 'center' : 'flex-start'
          }}>
            <Badge color="error" variant="dot" invisible={!hasFavorites}>
              <FavoriteIcon />
            </Badge>
          </ListItemIcon>
          {!isCompactMode && <ListItemText primary="Избранное" />}
          {isCompactMode && (
            <CompactTooltip title="Избранное" placement="right">
              <span />
            </CompactTooltip>
          )}
        </CategoryListItem>
      </Box>

      <Box sx={{ px: isCompactMode ? 1 : 2, pb: 1 }}>
        {!isCompactMode && (
          <Typography
            variant="subtitle2"
            color="text.secondary"
            fontWeight={600}
            sx={{ textTransform: 'uppercase', letterSpacing: 1 }}
          >
            Категории
          </Typography>
        )}
      </Box>

      <List sx={{ flex: 1, overflow: 'auto', px: isCompactMode ? 0 : 2 }}>
        {loading ? (
          <Box sx={{ py: 1 }}>
            {categorySkeleton}
          </Box>
        ) : error ? (
          <Typography color="error" sx={{ p: 2 }}>{error}</Typography>
        ) : (
          categories.map((category) => (
            <React.Fragment key={category.id}>
              <CategoryListItem
                onClick={(e) => {
                  e.stopPropagation();
                  if (isCompactMode) {
                    // В компактном режиме по клику переходим в категорию
                    handleCategoryNavigate(category.id);
                  } else {
                    // В обычном режиме разворачиваем категорию
                    handleCategoryClick(category.id, e);
                  }
                }}
                selected={isCategoryActive(category.id)}
                sx={{
                  py: isCompactMode ? 1.5 : undefined,
                  justifyContent: isCompactMode ? 'center' : 'flex-start',
                }}
              >
                <ActiveItemBackground />
                <ListItemIcon sx={{
                  color: isCategoryActive(category.id)
                    ? 'primary.main'
                    : 'text.secondary',
                  minWidth: isCompactMode ? 0 : 36,
                  mr: isCompactMode ? 0 : 2,
                  justifyContent: isCompactMode ? 'center' : 'flex-start'
                }}>
                  {getCategoryIcon(category.icon)}
                </ListItemIcon>
                {!isCompactMode && (
                  <>
                    <ListItemText
                      primary={category.title}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCategoryNavigate(category.id);
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCategoryClick(category.id, e);
                      }}
                      sx={{
                        color: isCategoryActive(category.id)
                          ? 'primary.main'
                          : 'text.secondary'
                      }}
                    >
                      {openCategories.includes(category.id) ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </>
                )}
                {isCompactMode && (
                  <CompactTooltip title={category.title} placement="right">
                    <span />
                  </CompactTooltip>
                )}
              </CategoryListItem>
              {!isCompactMode && (
                <Collapse in={openCategories.includes(category.id)} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {subcategories[category.id]?.map((subcategory) => (
                      <ListItemButton
                        key={subcategory.id}
                        onClick={() => handleSubcategoryClick(category.id, subcategory.id)}
                        selected={isSubcategoryActive(category.id, subcategory.id)}
                        sx={{
                          pl: 4,
                          mb: 0.5,
                          borderRadius: 2,
                          justifyContent: 'flex-start',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.08),
                            transform: 'translateX(4px)',
                          },
                          '&.Mui-selected': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            color: 'primary.main',
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.15),
                            },
                          },
                        }}
                      >
                        <ListItemText
                          primary={subcategory.title}
                          primaryTypographyProps={{
                            fontSize: '0.9rem',
                          }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{
                          color: isSubcategoryActive(category.id, subcategory.id)
                            ? 'primary.main'
                            : 'inherit'
                        }}>
                          {subcategory.articles_count}
                        </Typography>
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          ))
        )}
      </List>

      <Box
        sx={{
          mt: 'auto',
          p: isCompactMode ? 1 : 2,
          display: 'flex',
          justifyContent: isCompactMode ? 'center' : 'space-between',
          alignItems: 'center',
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          pt: 1.5,
          pb: 1.5,
        }}
      >
        {!isCompactMode && (
          <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.7 }}>
            Zerde Guide v1.0 (BETA)
          </Typography>
        )}

        <Tooltip title={isCompactMode ? "Развернуть меню" : "Свернуть меню"}>
          <IconButton
            onClick={toggleCompactMode}
            size="small"
            sx={{
              backgroundColor: alpha(theme.palette.background.default, 0.6),
              '&:hover': {
                backgroundColor: alpha(theme.palette.background.default, 0.9),
                transform: 'scale(1.1)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            {isCompactMode ?
              <FormatIndentIncreaseIcon fontSize="small" /> :
              <FormatIndentDecreaseIcon fontSize="small" />
            }
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <StyledAppBar position="fixed" drawerwidth={drawerWidth}>
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', position: 'relative' }}>
            <Tooltip title="Поиск по статьям">
              <Box sx={{ position: 'relative' }}>
                <NavIconButton onClick={() => navigate('/search')}>
                  <SearchIcon />
                </NavIconButton>
                <NavLinkIndicator isactive={(location.pathname === '/search').toString()} />
              </Box>
            </Tooltip>

            <Tooltip title="Избранное">
              <Box sx={{ position: 'relative' }}>
                <NavIconButton onClick={() => navigate('/favorites')}>
                  <Badge color="error" variant="dot" invisible={!hasFavorites}>
                    <FavoriteIcon />
                  </Badge>
                </NavIconButton>
                <NavLinkIndicator isactive={(location.pathname === '/favorites').toString()} />
              </Box>
            </Tooltip>

            <Tooltip title="Почта">
              <NavIconButton
                onClick={() => window.open("https://mail.zerdegroup.kz/", "_blank")}
                sx={{ ml: 1 }}
              >
                <EmailIcon />
              </NavIconButton>
            </Tooltip>


            <Tooltip title={mode === 'light' ? 'Тёмная тема' : 'Светлая тема'}>
              <NavIconButton onClick={toggleTheme} sx={{ ml: 1 }}>
                {mode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
              </NavIconButton>
            </Tooltip>
          </Box>
        </Toolbar>
        <ScrollProgressBar progress={scrollProgress} />
      </StyledAppBar>
      <Box
        component="nav"
        sx={{
          width: { sm: drawerWidth }, flexShrink: { sm: 0 },
          transition: 'width 0.3s ease-in-out'
        }}
      >
        <SwipeableDrawer
          variant="temporary"
          open={mobileOpen}
          onOpen={() => setMobileOpen(true)}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: '1px solid',
              borderColor: alpha(theme.palette.divider, 0.06),
              boxShadow: '0px 2px 30px rgba(0,0,0,0.08)',
            },
          }}
        >
          {drawer}
        </SwipeableDrawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: '1px solid',
              borderColor: alpha(theme.palette.divider, 0.06),
              boxShadow: mode === 'dark'
                ? 'none'
                : '0px 2px 30px rgba(0,0,0,0.04)',
              transition: theme.transitions.create(['width'], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              zIndex: theme.zIndex.drawer,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          backgroundColor: 'background.default',
          minHeight: '100vh',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout; 